"use client";

import { useEnrollmentRounds } from "@/themes/default/templates/section/enrollment/useEnrollmentRounds";

/**
 * dental06 enrollment banner — light clinical card (mist fill, sky hairline
 * border, navy-tinted shadow), amber-bar eyebrow, navy heading, amber pill
 * CTA + navy outline pill (ClinicalCarousel idiom).
 */
export function EnrollmentBanner({
  section,
  currentLanguage = "mm",
}: {
  section: any;
  currentLanguage?: "en" | "mm";
}) {
  const d = useEnrollmentRounds(section, currentLanguage);
  if (!d) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" lang={d.lang === "mm" ? "my" : "en"}>
      <div
        className="dental06-shadow overflow-hidden rounded-2xl border border-border"
        style={{ backgroundColor: "var(--dental06-mist)" }}
      >
        <div className="h-1 w-full" style={{ backgroundColor: "var(--dental06-sky)" }} aria-hidden="true" />
        {d.rounds.map((r, i) => (
          <div
            key={r.id}
            className={`flex flex-col gap-5 px-6 py-6 md:flex-row md:items-center md:px-9 md:py-7 ${
              i > 0 ? "border-t border-border" : ""
            }`}
          >
            <div className="min-w-0 flex-1">
              <p
                className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em]"
                style={{ color: "var(--dental06-blue)" }}
              >
                <span className="h-[3px] w-6 rounded-full" style={{ backgroundColor: "var(--color-accent)" }} aria-hidden="true" />
                {d.title}
              </p>
              <h2
                className="dental06-heading mt-2 text-xl md:text-2xl"
                style={{ color: "var(--dental06-navy)", lineHeight: d.lang === "mm" ? 1.6 : 1.3 }}
              >
                {r.label}
              </h2>
              {r.window && (
                <p className="mt-1 text-sm" style={{ color: "var(--color-muted-foreground)" }}>
                  {d.t.windowPrefix} {r.window}
                </p>
              )}
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              {r.listHref && (
                <a
                  href={r.listHref}
                  className="inline-flex items-center rounded-full border bg-white px-5 py-2.5 text-sm font-semibold text-[color:var(--dental06-navy)] transition hover:bg-[color:var(--dental06-navy-tint)]"
                  style={{ borderColor: "var(--color-border)" }}
                >
                  {d.t.list}
                </a>
              )}
              <a
                href={r.enrollHref}
                className="inline-flex items-center gap-2 rounded-full bg-[color:var(--color-accent)] px-6 py-2.5 text-sm font-semibold text-[color:var(--color-accent-foreground)] transition hover:brightness-95"
              >
                {d.t.enroll} <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
